import * as THREE from 'three'

/**
 * An AudioTrack manages the play/pause state of a audio file, as well as
 * recording, mutating and retrieving it's frequency data.
 *
 * @param {String} path
 * @param {Object} settings
 */
export default class AudioTrack {
  /**
   * A set of default options for the track.
   * @type {Object<String: Any>}
   */
  static #defaults = {
    clip: 0
  }

  /**
   * Contains a reference to all created AudioTrack instances.
   * @type {Array<AudioTrack>}
   */
  static #all = []

  /**
   * Tracks the first interaction with items tied to this global context
   * @type {Bool}
   */
  static #firstInteraction = true

  /**
   * Runs the AudioTrack.onFirstInteraction callback if it has never been run
   * before. Afterwards it resets it to a blank function.
   */
  static #tryFirstInteraction() {
    if (AudioTrack.#firstInteraction) {
      AudioTrack.#firstInteraction = false

      AudioTrack.onFirstInteraction()
      AudioTrack.onFirstInteraction = () => {}
    }
  }

  // GLOBAL AUDIO CONTEXT OBJECT -----------------------------------------------

  /**
   * A single AudioContext to use with all instances of AudioTrack
   * @type {AudioContext}
   */
  static #context = new AudioContext({
    latencyHint: 'playback'
  })

  /**
   * The state value of the underlying AudioContext.
   * @type {String}
   */
  static get contextState() {
    return AudioTrack.#context.state
  }

  // GLOBAL INSTANCE SETTINGS --------------------------------------------------

  /**
   * Defines the resolution of samples used with each AnalyserNode. Must be a
   * power of 2 between 2^5 and 2^15.
   * @type {Number}
   */
  static #FFT = Math.pow(2, 11)

  /**
   * The number of samples into the past used to smooth out data.
   * @type {Number}
   */
  static #averagingDepth = 3

  /**
   * The number of samples across frequencies to average together.
   * @type {Number}
   */
  static #averagingWidth = 12

  /**
   * Number of rows of samples of past data to store to display as a trail.
   * @type {Number}
   */
  static #historyDepth = 40

  /**
   * The amount of smoothing to apply to data (passed to the underlying
   * AnalyserNode). Accepts values in the range [0, 1].
   * @type {Number}
   */
  static #smoothing = 0.5

  /**
   * Stores the current connection state of the audio.
   * @type {Boolean}
   */
  #connected = false

  /**
   * A calculated property that stored whether the audio is effectively muted.
   * @type {Boolean}
   */
  get muted() {
    return !this.#connected || this.elem.paused
  }

  /**
   * A percentage of the frequency range to clip from the start. This allows
   * audio clips with less low-end to be cropped into a more interesting range.
   *
   * A value of 0 means that none of the data is clipped. 0.02 would that the
   * bottom 2% of frequencies are cropped out.
   *
   * @type {Number}
   */
  #clip

  /**
   * An AnalyserNode for this audio sample.
   * @type {AnalyserNode}
   */
  #analyser

  /**
   * The current frequency sample data.
   * @type {Float32Array}
   */
  get data() {
    return this.texture.image.data.subarray(0, this.dataLength)
  }

  // SETUP ---------------------------------------------------------------------

  constructor(path, settings) {
    this.path = path

    this.#analyser = AudioTrack.#context.createAnalyser()
    this.#analyser.fftSize = AudioTrack.#FFT
    this.#analyser.smoothingTimeConstant = AudioTrack.#smoothing

    let _clip = AudioTrack.#defaults.clip
    if ('clip' in settings) _clip = settings.clip
    this.#clip = Math.floor((_clip / 100) * this.#analyser.frequencyBinCount)

    this.dataLength = this.#analyser.frequencyBinCount - this.#clip

    this.rawData = new Float32Array(this.#analyser.frequencyBinCount).fill(this.#analyser.minDecibels)

    this.elem = new Audio()
    this.elem.setAttribute('src', this.path)
    this.elem.setAttribute('loop', '')
    this.elem.setAttribute('playsinline', '')
    document.body.appendChild(this.elem)

    this.texture = new THREE.DataTexture(
      new Float32Array(AudioTrack.#historyDepth * this.dataLength).fill(this.#analyser.minDecibels),
      this.dataLength,
      AudioTrack.#historyDepth,
      THREE.RedFormat,
      THREE.FloatType,
      THREE.UVMapping,
      THREE.MirroredRepeatWrapping,
      THREE.MirroredRepeatWrapping
    )
    this.texture.internalFormat = 'R32F'

    this.track = AudioTrack.#context.createMediaElementSource(this.elem)

    this.track.connect(AudioTrack.#context.destination)
    this.track.connect(this.#analyser)

    this.#connected = true

    AudioTrack.#all.push(this)
  }

  // EXTERNAL API --------------------------------------------------------------

  /**
   * Play the audio track.
   *
   * Checks the AudioContext state and resumes it if necessary.
   */
  play() {
    AudioTrack.#tryFirstInteraction()

    if (AudioTrack.#context.state !== 'running') {
      AudioTrack.wake()
    }

    this.elem.play()
  }

  /**
   * Pause the audio track.
   */
  pause() {
    AudioTrack.#tryFirstInteraction()

    this.elem.pause()
  }

  /**
   * Mute the track while maintaining analyser data.
   */
  mute() {
    AudioTrack.#tryFirstInteraction()

    this.track.disconnect(AudioTrack.#context.destination)
    this.#connected = false
  }

  /**
   * Unmute the track.
   */
  unmute() {
    AudioTrack.#tryFirstInteraction()

    this.track.connect(AudioTrack.#context.destination)
    this.#connected = true
  }

  /**
   * Calls mute() on all AudioTrack objects.
   */
  static muteAll() {
    AudioTrack.#all.forEach(audioTrack => audioTrack.mute())
  }

  /**
   * Calls unmute() on all AudioTrack objects.
   */
  static unmuteAll() {
    AudioTrack.#all.forEach(audioTrack => audioTrack.unmute())
  }

  /**
   * Suspends the global AudioContext.
   */
  static hibernate() {
    AudioTrack.#context.suspend()
  }

  /**
   * Resumes the global AudioContext.
   */
  static wake() {
    AudioTrack.#context.resume()
  }

  /**
   * Updates the frequency data for this audio sample, including changing
   * rawData and setting/updating the DataTexture.
   *
   * This is a multi-step process that shifts data in an array and then adds new
   * data to the front after it is processed.
   *
   * rawData is stored as a TypedArray with a length equal to the number of
   * frequency samples. Internally, texture is stored as a 1-dimensional
   * TypedArray of the same type, but represents a 2-dimensional image, so it's
   * length is rawData.length multiplied by the number of rows of data in the
   * image.
   *
   *           ┌───────────┐
   *     row 1 │ 9 6 1 0 3 │        row 1│    row 2│    row 3│     row 4│
   *     row 2 │ 1 1 3 2 6 │             │         │         │          │
   *     row 3 │ 6 3 8 5 2 │  ┌──────────┴─────────┴─────────┴──────────┤
   *     row 4 │ 0 0 0 2 1 │  │ 9 6 1 0 3 1 1 3 2 6 6 3 8 5 2 0 0 0 2 1 │
   *           └───────────┘  └─────────────────────────────────────────┘
   *
   * For each call to update(), the texture image data needs to shift down one
   * row, and an new row needs to be appended to the top (keeping the overall
   * dimensions the same).
   *                             ┌ ─ ─ ─ ─ ─ ┐
   *                   new data──○ 1 2 3 2 1 │
   *                             └ ─ ─ ┬ ─ ─ ┘
   *               ┌───────────┐ ┌─────▼─────┐ ┌───────────┐
   *               │ 9 6 1 0 3 │ │ ░ ░ ░ ░ ░ │ │ 1 2 3 2 1 │
   *               │ 1 1 3 2 6 │ │ 9 6 1 0 3 │ │ 9 6 1 0 3 │
   *               │ 6 3 8 5 2 │ │ 1 1 3 2 6 │ │ 1 1 3 2 6 │
   *               │ 0 0 0 2 1 │ │ 6 3 8 5 2 │ │ 6 3 8 5 2 │
   *               └───────────┘ └─────┬─────┘ └───────────┘
   *                             ┌ ─ ─ ▼ ─ ─ ┐
   *                             │ 0 0 0 2 1 ○──discarded data
   *                             └ ─ ─ ─ ─ ─ ┘
   *
   * ...which would translate to this transformation in 2D
   *
   *                     row 1│    row 2│    row 3│     row 4│
   *               ┌──────────┴─────────┴─────────┴──────────┤
   *               │ 9 6 1 0 3 1 1 3 2 6 6 3 8 5 2 0 0 0 2 1 │
   *               └──────────┬─────────┬─────────┬──────────┤
   * ┌ ─ ─ ─ ─ ─ ┐ ┌──────────┴─────────┴─────────┴──────────┤ ┌ ─ ─ ─ ─ ─ ┐
   * │ 1 2 3 2 1 ──▶ ░ ░ ░ ░ ░ 9 6 1 0 3 1 1 3 2 6 6 3 8 5 2 ├─▶ 0 0 0 2 1 │
   * └ ─ ─ ─ ─ ─ ┘ └──────────┬─────────┬─────────┬──────────┤ └ ─ ─ ─ ─ ─ ┘
   *               ┌──────────┴─────────┴─────────┴──────────┤
   *               │ 1 2 3 2 1 9 6 1 0 3 1 1 3 2 6 6 3 8 5 2 │
   *               └─────────────────────────────────────────┘
   */
  update() {
    this.#analyser.getFloatFrequencyData(this.rawData)

    // 1. Move history data up one row
    this.texture.image.data.set(
      this.texture.image.data.slice(0, this.texture.image.data.length - this.dataLength),
      this.dataLength
    )

    this.texture.image.data.set(
      // 6. Set the data from steps 2-5 as the first row of data in our texture
      this.rawData
        // 2. Remove cropped frequencies from rawData
        .slice(this.#clip)
        // 3. Remove values of -Infinity and Infinity
        .map(this.#removeInfinity)
        // 4. Average data across frequency range
        .map(this.#averageHorizontal)
        // 5. Average data with history
        .map(this.#averageVertical),
      0
    )

    this.texture.needsUpdate = true
  }

  /**
   * A callback that is triggered whenever AudioContext.state changes.
   */
  static onFirstInteraction = () => {}

  // DATA PROCESSING -----------------------------------------------------------

  /**
   * Removes values of Infinity and -Infinite from frequency data.
   *
   * Used as the function callback for Array.map().
   *
   * @param {Number} value - Value to average
   * @return {Number}
   */
  #removeInfinity = (value) => {
    if (value === -Infinity) {
      return this.#analyser.minDecibels
    } else if (value === Infinity) {
      return this.#analyser.maxDecibels
    }
    return Math.max(this.#analyser.minDecibels, Math.min(this.#analyser.maxDecibels, value))
  }

  /**
   * Averages audio data across frequencies in the current frame.
   *
   * Used as the function callback for Array.map().
   *
   * @param {Number} value - Value to average
   * @param {Number} i - The value's index in it's parent array
   * @param {Array<Number>} arr - The parent array
   * @return {Number}
   */
  #averageHorizontal = (value, i, arr) => {
    if (AudioTrack.#averagingWidth === 0) return value
    return AudioTrack.#movingAverage(
      arr.subarray(
        Math.max(i - AudioTrack.#averagingWidth, 0),
        Math.min(i + AudioTrack.#averagingWidth, arr.length)
      ),
      true
    )
  }

  /**
   * Averages audio data history.
   *
   * Used as the function callback for Array.map().
   *
   * @param {Number} value - Value to average
   * @param {Number} i - The value's index in it's parent array
   * @return {Number}
   */
  #averageVertical = (value, i) => {
    if (AudioTrack.#averagingDepth === 0) return value
    const values = [value]
    for (let j = 1; j < Math.min(AudioTrack.#historyDepth, AudioTrack.#averagingDepth + 1); j++) {
      values.push(this.texture.image.data[j * this.dataLength + i])
    }
    return AudioTrack.#movingAverage(values)
  }

  /**
   * Calculates an average from a range of data. Can optionally weight the
   * average in favor of more recent values.
   * @param {Array<Number>} values - The data to average
   * @param {Boolean} weighted
   * @return {Number}
   */
  static #movingAverage(values, weighted) {
    if (weighted === true) {
      let acc = 0
      values.forEach((v, i) => {
        acc += v * (values.length - i)
      })
      return acc / ((values.length * (values.length + 1)) / 2)
    }
    return values.reduce(
      (acc, val) => acc + val,
      0
    ) / values.length
  }
}
